import { useMemo } from "react";
import type { AssignmentMode, DraftMode } from "./expeditionsBoxFilters";
import { useUrlFiltersReducer } from "@/hooks/useUrlSyncedFilters";
import {
  booleanCodec,
  dayRangeCodec,
  enumCodec,
  nullableBooleanCodec,
  nullableEnumCodec,
  nullableStringCodec,
  readFiltersFromUrl,
  stringCodec,
  type UrlFilterCodecs,
} from "@/lib/urlFilters.util";
import {
  dayFilter,
  getWeekStart,
  rangeFilter,
  resolveDateFilterType,
  weekFilter,
  weekNavigationFilter,
  weekStartCodec,
  type DateFilterPatch,
  type DateFilterType,
} from "@/components/backOffice/shared/dateFilter.util";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { COUNTRY_REGIONS, type RegionCode } from "@/constants/countries";

export type PickupDateFilterType = Exclude<DateFilterType, "MONTH">;

export interface ExpeditionsBoxFilters {
  assigneeId: string | null;
  weekStart: Date;
  pickupDateAt: [Date, Date] | null;
  pickupDateFilterType: PickupDateFilterType | null;
  isCustomQuotation: boolean;
  assignmentMode: AssignmentMode;
  isLate: boolean;
  isExpress: boolean;
  isRemote: boolean;
  isDelayed: boolean;
  isManyBottles: boolean;
  country: string | null;
  draftMode: DraftMode;
  search: string;
  searchExact: boolean;
  customId: string | null;
  pickupZoneId: string | null;
  packagingType: PackagingFilterEnum | null;
  region: RegionCode | null;
  deliveryFromDateAfter: string | null;
  deliveryFromDateBefore: string | null;
  isReExpedition: boolean | null;
}

type FilterAction =
  | { type: "SET_ASSIGNEE_ID"; payload: string | null }
  | { type: "SET_WEEK_START"; payload: Date }
  | { type: "SET_PICKUP_DAY"; payload: Date | null }
  | { type: "SET_PICKUP_WEEK"; payload: { active: boolean; weekStart: Date } }
  | { type: "SET_PICKUP_DATE_RANGE"; payload: [Date, Date] | null }
  | { type: "TOGGLE_CUSTOM_QUOTATION" }
  | { type: "SET_ASSIGNMENT_MODE"; payload: AssignmentMode }
  | { type: "TOGGLE_LATE" }
  | { type: "TOGGLE_EXPRESS" }
  | { type: "TOGGLE_REMOTE" }
  | { type: "TOGGLE_DELAYED" }
  | { type: "TOGGLE_MANY_BOTTLES" }
  | { type: "SET_COUNTRY"; payload: string | null }
  | { type: "SET_DRAFT_MODE"; payload: DraftMode }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_CUSTOM_ID"; payload: string | null }
  | { type: "SET_PICKUP_ZONE_ID"; payload: string | null }
  | { type: "SET_PACKAGING_TYPE"; payload: PackagingFilterEnum | null }
  | { type: "SET_REGION"; payload: RegionCode | null }
  | {
      type: "SET_DELIVERY_FROM_DATE_RANGE";
      payload: { after: string | null; before: string | null };
    }
  | { type: "SET_IS_RE_EXPEDITION"; payload: boolean | null }
  | { type: "RESET_TABLE_FILTERS" };

const toPickupDate = (patch: DateFilterPatch<PickupDateFilterType>) => ({
  pickupDateAt: patch.dateAt,
  pickupDateFilterType: patch.dateFilterType,
});

const getInitialState = (): ExpeditionsBoxFilters => ({
  assigneeId: null,
  weekStart: getWeekStart(new Date()),
  pickupDateAt: null,
  pickupDateFilterType: null,
  isCustomQuotation: false,
  assignmentMode: "all",
  isLate: false,
  isExpress: false,
  isRemote: false,
  isDelayed: false,
  isManyBottles: false,
  country: null,
  draftMode: "all",
  search: "",
  searchExact: false,
  customId: null,
  pickupZoneId: null,
  packagingType: null,
  region: null,
  deliveryFromDateAfter: null,
  deliveryFromDateBefore: null,
  isReExpedition: null,
});

function filterReducer(state: ExpeditionsBoxFilters, action: FilterAction): ExpeditionsBoxFilters {
  switch (action.type) {
    case "SET_ASSIGNEE_ID":
      return { ...state, assigneeId: action.payload };
    case "SET_WEEK_START": {
      const weekStart = action.payload;
      const patch = weekNavigationFilter(state.pickupDateFilterType, weekStart);
      return { ...state, weekStart, ...(patch ? toPickupDate(patch) : {}) };
    }
    case "SET_PICKUP_DAY":
      return { ...state, ...toPickupDate(dayFilter(action.payload)) };
    case "SET_PICKUP_WEEK":
      return {
        ...state,
        ...toPickupDate(weekFilter(action.payload.active, action.payload.weekStart)),
      };
    case "SET_PICKUP_DATE_RANGE":
      return { ...state, ...toPickupDate(rangeFilter(action.payload)) };
    case "TOGGLE_CUSTOM_QUOTATION":
      return { ...state, isCustomQuotation: !state.isCustomQuotation };
    case "SET_ASSIGNMENT_MODE":
      return { ...state, assignmentMode: action.payload };
    case "TOGGLE_LATE":
      return { ...state, isLate: !state.isLate };
    case "TOGGLE_EXPRESS":
      return { ...state, isExpress: !state.isExpress };
    case "TOGGLE_REMOTE":
      return { ...state, isRemote: !state.isRemote };
    case "TOGGLE_DELAYED":
      return { ...state, isDelayed: !state.isDelayed };
    case "TOGGLE_MANY_BOTTLES":
      return { ...state, isManyBottles: !state.isManyBottles };
    // `country` and `region` are exclusive: the query gives priority to the country,
    case "SET_COUNTRY":
      return { ...state, country: action.payload, region: action.payload ? null : state.region };
    case "SET_DRAFT_MODE":
      return { ...state, draftMode: action.payload };
    case "SET_SEARCH":
      return { ...state, search: action.payload, searchExact: false };
    case "SET_CUSTOM_ID":
      return { ...state, customId: action.payload };
    case "SET_PICKUP_ZONE_ID":
      return { ...state, pickupZoneId: action.payload };
    case "SET_PACKAGING_TYPE":
      return { ...state, packagingType: action.payload };
    case "SET_REGION":
      return { ...state, region: action.payload, country: null };
    case "SET_DELIVERY_FROM_DATE_RANGE":
      return {
        ...state,
        deliveryFromDateAfter: action.payload.after,
        deliveryFromDateBefore: action.payload.before,
      };
    case "SET_IS_RE_EXPEDITION":
      return { ...state, isReExpedition: action.payload };
    case "RESET_TABLE_FILTERS":
      return getInitialState();
    default:
      return state;
  }
}
const PICKUP_DATE_FILTER_TYPES: PickupDateFilterType[] = ["DAY", "WEEK", "RANGE"];
const ASSIGNMENT_MODES: readonly AssignmentMode[] = ["all", "mine"];
const DRAFT_MODES: readonly DraftMode[] = ["all", "only", "onlyMine", "overdue48h", "exclude"];
const PACKAGING_TYPES = Object.values(PackagingFilterEnum);
const REGION_CODES = Object.keys(COUNTRY_REGIONS) as RegionCode[];

const FILTER_CODECS: UrlFilterCodecs<ExpeditionsBoxFilters> = {
  assigneeId: nullableStringCodec("assignee"),
  weekStart: weekStartCodec(),
  pickupDateAt: dayRangeCodec("pickupRange"),
  pickupDateFilterType: nullableEnumCodec("pickupType", PICKUP_DATE_FILTER_TYPES),
  isCustomQuotation: booleanCodec("customQuotation"),
  assignmentMode: enumCodec("assignment", ASSIGNMENT_MODES, "all"),
  isLate: booleanCodec("late"),
  isExpress: booleanCodec("express"),
  isRemote: booleanCodec("remote"),
  isDelayed: booleanCodec("delayed"),
  isManyBottles: booleanCodec("manyBottles"),
  country: nullableStringCodec("country"),
  draftMode: enumCodec("draft", DRAFT_MODES, "all"),
  search: stringCodec("search"),
  searchExact: booleanCodec("exact"),
  customId: nullableStringCodec("customId"),
  pickupZoneId: nullableStringCodec("pickupZone"),
  packagingType: nullableEnumCodec("packaging", PACKAGING_TYPES),
  region: nullableEnumCodec("region", REGION_CODES),
  deliveryFromDateAfter: nullableStringCodec("deliveryAfter"),
  deliveryFromDateBefore: nullableStringCodec("deliveryBefore"),
  isReExpedition: nullableBooleanCodec("reExpedition"),
};

function getInitialStateFromUrl(searchParams: URLSearchParams): ExpeditionsBoxFilters {
  const state = readFiltersFromUrl(searchParams, getInitialState(), FILTER_CODECS);
  if (state.country) state.region = null;
  state.pickupDateFilterType = resolveDateFilterType(
    state.pickupDateAt,
    state.pickupDateFilterType,
  );
  return state;
}

export const useExpeditionsBoxFilters = () => {
  const { filters, debouncedSearch, dispatch } = useUrlFiltersReducer(
    filterReducer,
    FILTER_CODECS,
    getInitialStateFromUrl,
  );

  const actions = useMemo(
    () => ({
      setAssigneeId: (id: string | null) => dispatch({ type: "SET_ASSIGNEE_ID", payload: id }),
      setWeekStart: (weekStart: Date) => dispatch({ type: "SET_WEEK_START", payload: weekStart }),
      setPickupDay: (day: Date | null) => dispatch({ type: "SET_PICKUP_DAY", payload: day }),
      setPickupWeek: (active: boolean, weekStart: Date) =>
        dispatch({ type: "SET_PICKUP_WEEK", payload: { active, weekStart } }),
      setPickupDateRange: (range: [Date, Date] | null) =>
        dispatch({ type: "SET_PICKUP_DATE_RANGE", payload: range }),
      toggleCustomQuotation: () => dispatch({ type: "TOGGLE_CUSTOM_QUOTATION" }),
      setAssignmentMode: (mode: AssignmentMode) =>
        dispatch({ type: "SET_ASSIGNMENT_MODE", payload: mode }),
      toggleLate: () => dispatch({ type: "TOGGLE_LATE" }),
      toggleExpress: () => dispatch({ type: "TOGGLE_EXPRESS" }),
      toggleRemote: () => dispatch({ type: "TOGGLE_REMOTE" }),
      toggleDelayed: () => dispatch({ type: "TOGGLE_DELAYED" }),
      toggleManyBottles: () => dispatch({ type: "TOGGLE_MANY_BOTTLES" }),
      setCountry: (country: string | null) => dispatch({ type: "SET_COUNTRY", payload: country }),
      setDraftMode: (mode: DraftMode) => dispatch({ type: "SET_DRAFT_MODE", payload: mode }),
      setSearch: (search: string) => dispatch({ type: "SET_SEARCH", payload: search }),
      setCustomId: (id: string | null) => dispatch({ type: "SET_CUSTOM_ID", payload: id }),
      setPickupZoneId: (id: string | null) => dispatch({ type: "SET_PICKUP_ZONE_ID", payload: id }),
      setPackagingType: (type: PackagingFilterEnum | null) =>
        dispatch({ type: "SET_PACKAGING_TYPE", payload: type }),
      setRegion: (region: RegionCode | null) => dispatch({ type: "SET_REGION", payload: region }),
      setDeliveryFromDateRange: (after: string | null, before: string | null) =>
        dispatch({ type: "SET_DELIVERY_FROM_DATE_RANGE", payload: { after, before } }),
      setIsReExpedition: (value: boolean | null) =>
        dispatch({ type: "SET_IS_RE_EXPEDITION", payload: value }),
      resetTableFilters: () => dispatch({ type: "RESET_TABLE_FILTERS" }),
    }),
    [dispatch],
  );

  return useMemo(
    () => ({ filters, debouncedSearch, actions }),
    [filters, debouncedSearch, actions],
  );
};
